import React, { ReactNode, useContext } from 'react'
import { getIn, useFormikContext } from 'formik'
import DecimalInput from '../shared/decimalInput'
import FieldFrame from '../shared/forms/FieldFrame'
import { FormReadOnlyContext } from '../shared/forms/FormReadOnlyContext'
import { useFieldError } from '../shared/forms/useFieldError'
import { t } from '../../i18n'
import { FeederDosingModeValues } from './feederDosingModeValues'

export type Unit = 'bar' | 'hz' | 'percent' | 's' | 'g'

export const modeLabel = (key: string, options?: Record<string, number>) =>
  t(`feeder_dosing_modes.${key}`, options)

const unitLabel = (unit: Unit) => t(`feeder_dosing_modes.units.${unit}`)

const invalidClass = (error?: string) => (error ? 'is-invalid' : '')

interface NumberFieldProps {
  name: string
  label?: string
  // Spoken name for inputs whose label is a table header rather than a <label>
  ariaLabel?: string
  unit?: Unit
  placeholder?: string
  // Fractional digits accepted; 0 for whole numbers, omitted for any decimal
  decimals?: number
  disabled?: boolean
  // White input for fields sitting on the grey card rather than inside a white panel
  inverted?: boolean
}

export const NumberField = ({
  name,
  label,
  ariaLabel,
  unit,
  placeholder,
  decimals,
  disabled = false,
  inverted = false
}: NumberFieldProps) => {
  const { values, setFieldValue, setFieldTouched } =
    useFormikContext<FeederDosingModeValues>()
  const { error, clearServerError, describedBy, invalid } = useFieldError(name)
  const readOnly = useContext(FormReadOnlyContext)
  const input = (
    <DecimalInput
      id={name}
      name={name}
      value={getIn(values, name)}
      decimals={decimals}
      emptyValue={null}
      placeholder={placeholder}
      disabled={disabled || readOnly}
      ariaLabel={ariaLabel}
      ariaInvalid={invalid}
      ariaDescribedBy={describedBy}
      className={`form-control ${inverted ? 'inverted' : ''} ${invalidClass(error)}`}
      onChange={(fieldName, value) => {
        clearServerError()
        setFieldTouched(fieldName, true, false)
        setFieldValue(fieldName, value)
      }}
    />
  )

  return (
    <FieldFrame name={name} label={label} error={error}>
      {unit ? (
        <div className="field-with-unit">
          {input}
          <span className="field-with-unit__unit">{unitLabel(unit)}</span>
        </div>
      ) : (
        input
      )}
    </FieldFrame>
  )
}

// A white panel; `toggle` sits next to the title, for panels a switch turns on and off
export const Panel = ({
  title,
  toggle,
  children
}: {
  title?: string
  toggle?: ReactNode
  children: ReactNode
}) => (
  <div className="dosing-mode-form__panel">
    {(title || toggle) && (
      <div className="dosing-mode-form__panel-header">
        {title && <h6 className="dosing-mode-form__panel-title">{title}</h6>}
        {toggle}
      </div>
    )}
    {children}
  </div>
)

// A divided part of a panel with its own title, for settings that belong to the panel's dosing phase
export const PanelSection = ({
  title,
  hint,
  toggle,
  children
}: {
  title: string
  hint?: string
  toggle?: ReactNode
  children: ReactNode
}) => (
  <div className="dosing-mode-form__subsection">
    <div className="dosing-mode-form__panel-header">
      <div>
        <h6 className="dosing-mode-form__panel-title">{title}</h6>
        {hint && <div className="dosing-mode-form__hint">{hint}</div>}
      </div>
      {toggle}
    </div>
    {children}
  </div>
)

export const ParameterFields = ({
  prefix,
  disabled = false
}: {
  prefix: string
  disabled?: boolean
}) => (
  <>
    <NumberField
      name={`${prefix}.pressure`}
      label={modeLabel('parameters.pressure')}
      unit="bar"
      disabled={disabled}
    />
    <NumberField
      name={`${prefix}.frequency`}
      label={modeLabel('parameters.frequency')}
      unit="hz"
      disabled={disabled}
    />
    <NumberField
      name={`${prefix}.duty_cycle`}
      label={modeLabel('parameters.duty_cycle')}
      unit="percent"
      disabled={disabled}
    />
  </>
)
